import {Injectable} from "@angular/core";
import {NotificationService} from "../notification/notification.service";
import {Subscription} from "rxjs";
import {EntityData} from "@fedelta-pos/data-client";
import {LoadingService} from "../ui-elements/loading/loading.service";
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {OrderModel} from "../order/order.model";
import {OrderingContextModel} from "./ordering-context.model";
import {SessionModel} from "../orchestration/session.model";
import {NavigationService} from "../orchestration/navigation.service";
import {OrderingContext, OrderingContextIntention} from "./ordering-context";
import {OrderingContextEvents} from "./ordering-context-events";
import {StoreData} from "../store/store-search.model";
import {OrderType} from "../order/order";

@Injectable()
export class OrderingContextController {
    constructor(private sessionModel: SessionModel,
                private orderingContextModel: OrderingContextModel,
                private orderModel: OrderModel,
                private notificationService: NotificationService,
                private loadingService: LoadingService,
                private navigationService: NavigationService) {
    }

    public async loadStoresForSelection(orderingContext: OrderingContext): Promise<boolean> {
        await this.sessionModel.loadApp();
        await this.orderingContextModel.loadStores(true);

        // No stores - go to the appropriate page, and it will inform the user
        if (!this.orderingContextModel.stores) {
            // noinspection ES6MissingAwait
            this.selectAndApplyContext(orderingContext.withIntention(OrderingContextIntention.ORDER));
            return false;
        }
        return true;
    }

    public async viewStores(): Promise<void> {
        return this.selectAndApplyContext(new OrderingContext()
            .withIntention(OrderingContextIntention.VIEW_STORES)
            .withSelectedOrderType(this.orderingContextModel.orderType)
            .withSelectedTable(this.orderingContextModel.preselectedTableSelection));
    }

    /**
     * View the current store's information page
     */
    public async viewCurrentStore(): Promise<void> {
        if (!this.orderingContextModel.currentStore && !this.orderingContextModel.singleStoreLocation) {
            return;
        }
        return this.navigationService.createPage("woa-store-details-page", {
            orderingContext: new OrderingContext()
                .withIntention(OrderingContextIntention.VIEW_CURRENT_STORE)
                .withSelectedStore(this.orderingContextModel.currentStore)
        });
    }

    public async changeOrderingContext(timeOnly: boolean): Promise<void> {

        // There is only one possible action for time-only mode.
        // This method should not get called if the preconditions are not met, but we check anyway.
        if (timeOnly) {
            return this.orderingContextModel.canSelectOrderTime ? this.changeExpectedCompletion() : undefined;
        }

        // If we don't have both an order-type and a store already selected, then we leave store and/or order-type selection
        // up to the ordering-context-selection page. It will prompt for each one as required, and will not require pre-selection
        // of order type unless necessary (i.e. order type can be selected from the store list).
        if (!this.orderingContextModel.orderType || !this.orderingContextModel.currentStore) {
            return this.changeStoreOrOrderType();
        }

        // When neither store nor order type can be changed (usually the case for most venues), we can bypass the ordering context edit page for pickup or table orders.
        // For table orders, we go straight to table selection.
        // For pickup, we go straight to time selection.
        if (this.orderingContextModel.currentStore && this.orderingContextModel.singleOrderType && !this.orderingContextModel.canChangeStore) {
            if (this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE) {
                return this.changeFulfilmentDetails();
            }
            if (this.orderingContextModel.orderType === OrderType.ORDER_AHEAD) {
                return this.orderingContextModel.canSelectOrderTime ? this.changeExpectedCompletion() : undefined;
            }
        }

        this.orderModel.orderErrorStates.clearOrderingContextErrors();
        await this.untilExpectedCompletionEvaluationComplete();
        return this.navigationService.createPage("woa-ordering-context-edit-page");
    }

    public async changeOrderType(): Promise<void> {
        this.orderModel.orderErrorStates.clearOrderingContextErrors();
        await this.selectAndApplyStoreAndOrderType(new OrderingContext()
            .withIntention(OrderingContextIntention.SELECT_ORDER_TYPE));
    }

    public async changeStoreOrOrderType(orderType?: OrderType): Promise<void> {
        this.orderModel.orderErrorStates.clearOrderingContextErrors();
        await this.untilExpectedCompletionEvaluationComplete();
        await this.selectAndApplyStoreAndOrderType(new OrderingContext()
            .withIntention(OrderingContextIntention.SELECT_STORE)
            .withDefaultOrderType(orderType)
            // For store groups, we don't inform the selection page of the order type. This causes it to ask for store selection first, rather than table number.
            .withSelectedOrderType(orderType ? orderType : this.orderingContextModel.hasStoreGroup ? undefined : this.orderingContextModel.orderType)
            .withSelectedTable(this.orderingContextModel.preselectedTableSelection));
    }

    public async changeFulfilmentDetails(): Promise<any> {

        // For a single store-location, store selection cannot change.
        if (this.orderingContextModel.singleStoreLocation) {
            return this.selectAndApplyContext(new OrderingContext()
                .withIntention(OrderingContextIntention.CHANGE_FULFILMENT_DETAILS)
                .withDefaultOrderType(this.orderingContextModel.orderType)
                .withSelectedOrderType(this.orderingContextModel.orderType));
        }

        // For multi-store, delivery address or table changes may result in a new store being selected
        return this.selectAndApplyStoreAndOrderType(new OrderingContext()
            .withIntention(OrderingContextIntention.CHANGE_FULFILMENT_DETAILS)
            .withSelectedStore(this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE ? undefined : this.orderingContextModel.currentStore)
            .withDefaultOrderType(this.orderingContextModel.orderType)
            .withSelectedOrderType(this.orderingContextModel.orderType)
            .withSelectedTable(this.orderingContextModel.preselectedTableSelection));
    }

    public async changeExpectedCompletion(): Promise<void> {
        const previousExpectedCompletion: Date = this.orderingContextModel.expectedCompletion;
        if (!await this.navigationService.createPage("woa-order-time-selection-page", {
            hideAsap: !this.orderingContextModel.expectedCompletion && this.orderModel.cannotAllocateTime
        })) {
            return;
        }

        if (this.orderModel.initiatedOrder) {
            this.orderModel.initiatedOrder.estimatedPickupLocalDateTime = undefined;
            this.orderModel.initiatedOrder.estimatedCompletionLocalDateTime = undefined;
            this.orderModel.initiatedOrder.cannotAllocateOrderLaterDateTime = false;
        }

        return this.loadingService.performWithSpinner(async () => {
            OrderingContextEvents.evaluatingExpectedCompletionChange$.next(true);
            try {
                await this.orderingContextModel.confirmExpectedCompletionChange(previousExpectedCompletion);
                await this.orderingContextModel.loadStores(true);
                OrderingContextEvents.evaluatingExpectedCompletionChange$.next(false);
            } catch (error) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.notification.expectedCompletionCouldNotBeChanged"));
                OrderingContextEvents.evaluatingExpectedCompletionChange$.next(false);
                console.log(error);
            }
        });
    }

    /**
     * Selects a store and/or order type as necessary, and applies them to the session
     * @param orderingContext
     */
    public async selectAndApplyStoreAndOrderType(orderingContext: OrderingContext = new OrderingContext()): Promise<any> {
        if (!this.orderingContextModel.stores && !await this.loadStoresForSelection(orderingContext)) {
            return;
        }

        // If there is only one store-location, select or change the order type,
        // and select the store automatically based on the order type.
        let orderType: OrderType = orderingContext.selectedOrderType || orderingContext.defaultOrderType;

        if (!this.orderingContextModel.singleStoreLocation && this.orderingContextModel.currentStore && orderType && !this.orderingContextModel.orderType) {
            return this.orderingContextModel.applyOrderingContext(orderingContext
                .withSelectedStore(this.orderingContextModel.getCurrentStoreForOrderType(orderType))
                .withSelectedOrderType(orderType));
        }

        if (this.orderingContextModel.singleStoreLocation && orderType) {
            const newStore: StoreData = this.orderingContextModel.singleStoreLocation.getStoreForOrderType(orderType);
            if (newStore) {
                return this.orderingContextModel.applyOrderingContext(orderingContext
                    .withSelectedOrderType(orderType)
                    .withSelectedStore(newStore));
            }
            return;
        }

        if (this.orderingContextModel.singleStoreLocation || (this.orderingContextModel.currentStore && !orderType && !!orderingContext.selectedMenuItem)) {
            orderingContext = await this.navigationService.createPage("woa-ordering-context-selection-page", {
                orderingContext: orderingContext
                    .withIntention(OrderingContextIntention.SELECT_ORDER_TYPE)
            });
            if (orderingContext) {
                const newStore: StoreData = this.orderingContextModel.singleStoreLocation
                    ? this.orderingContextModel.singleStoreLocation.getStoreForOrderType(orderingContext.selectedOrderType)
                    : this.orderingContextModel.getCurrentStoreForOrderType(orderingContext.selectedOrderType);
                if (newStore) {
                    return this.orderingContextModel.applyOrderingContext(orderingContext
                        .withSelectedStore(newStore));
                }
            }
            return;
        }

        // If switching order type to pickup or table order, and the current selected store in the session model supports the new order type,
        // and (for table order) if there is a preselected table number, change the order type and retain the selected store.
        const orderTypeChange: boolean = orderType && orderType !== this.orderingContextModel.orderType;
        if (orderTypeChange && (orderType === OrderType.ORDER_AHEAD || (orderType === OrderType.ORDER_AT_TABLE && this.orderingContextModel.preselectedTableNumber)) &&
            this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.canOrder(orderType)) {
            const newStore: StoreData = <StoreData>EntityData.create(StoreData).assign(this.orderingContextModel.currentStore);
            newStore.tableNumber = undefined;
            return this.orderingContextModel.applyOrderingContext(orderingContext
                .withSelectedStore(newStore)
                .withSelectedOrderType(orderType));
        }

        // Warn the user that their order will be cleared if the store changes
        if (this.orderModel.hasOrderedItems() &&
            !(this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE && orderingContext.intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS) &&
            !await this.notificationService.displayConfirmation("woa.heading.ordering-context.confirmStoreChange", undefined,
                "woa.message.ordering-context.confirmStoreChange",
                "woa.button.common.noThanks", "woa.button.common.proceed", true)) {
            return;
        }

        return this.selectAndApplyContext(orderType === OrderType.ORDER_AT_TABLE
            ? orderingContext.withSelectedOrderType(orderType)
            : orderingContext.withDefaultOrderType(orderType));
    }

    /**
     * Launches the ordering context selection page and applies the ordering context (if any) returned by that page.
     *
     * @param orderingContext The seed ordering context
     */
    private async selectAndApplyContext(orderingContext: OrderingContext): Promise<any> {
        orderingContext = await this.navigationService.createPage("woa-ordering-context-selection-page", {
            orderingContext: orderingContext
        });
        if (orderingContext && await this.orderingContextModel.applyOrderingContext(orderingContext) && !this.orderingContextModel.menuId && !orderingContext.selectedStore) {
            return this.selectAndApplyStoreAndOrderType(orderingContext.withSelectedTable(this.orderingContextModel.preselectedTableSelection, false));
        }
    }

    public async untilExpectedCompletionEvaluationComplete(): Promise<void> {
        if (!OrderingContextEvents.evaluatingExpectedCompletionChange$.getValue()) {
            return;
        }

        await this.loadingService.performWithSpinner(async () => await new Promise<void>((resolve) => {
            if (!OrderingContextEvents.evaluatingExpectedCompletionChange$.getValue()) {
                resolve();
            } else {
                const subscription: Subscription = OrderingContextEvents.evaluatingExpectedCompletionChange$.subscribe((evaluating: boolean) => {
                    if (!evaluating) {
                        subscription.unsubscribe();
                        resolve();
                    }
                });
            }
        }), undefined, "woa.message.order-time.evaluatingTimes");
    }
}



// WEBPACK FOOTER //
// ./src/app/ordering-context/ordering-context.controller.ts